import React from "react";
import Layout from "../../components/base/blog-layout";
import SEO from "../../components/base/seo";
import headerImage from "../../images/blog/TSD_Blog_Hero_Holiday_Shopping_Season.jpg";
import pageImage from "../../images/blog/TSD_Blog_Overview_Holiday_Shopping_Season.jpg";

const BlogPostHolidayShoppingSeason = () => (
  <Layout>
    <SEO
      title="The Holiday Shopping Season Is Coming!"
      description="The holiday shopping season is upon us! Make the most of your Q4 with these seasonal marketing tips from our marketing nerds."
    />

    {/* Header image and top info section */}
    <main className="template-resources">
      <div className="single-resource-post">
        <section className="page-heading">
          <div className="container">
            <div
              className="single-resource-header-image"
              style={{ backgroundImage: `url(${headerImage})` }}
            ></div>
          </div>
        </section>

        <section>
          <p className="single-resource-top-info container">
            The Simple Department | September 30, 2022
          </p>

          <div className="container blog-content-wrapper">
            <h1>The Holiday Shopping Season Is Coming!</h1>

            <p>
              We officially had our first rainy Fall day after an unseasonably
              warm September on the West Coast, and it immediately made the whole
              team think of pumpkin spice lattes, cozy movie nights and, of course,
              the marketing for the upcoming holiday shopping season. Q4 (or the 4th
              financial quarter of the year) officially starts on Saturday and, for
              many, it is the most profitable 3 months of the year, with some
              companies making up to 40% of their annual profits between October 1st
              and December 31st.
            </p>

            <p>
              The looming holiday shopping season might feel overwhelming, especially
              in the face of a global downturn, but The Simple Department has your
              back. We’ve put together 4 simple tips to prepare your marketing for the
              holiday shopping season (plus a part two with even more tips, coming in a
              couple weeks!).
            </p>

            <h2>Seasonal-themed Product Offerings</h2>
            <p>
              For many, the changing seasons signal to people that it’s time to switch
              up their wardrobe, eating habits, makeup routine, and more. People{" "}
              <em>fall</em> (pun intended) hard into Autumn and the holiday season as
              a whole, and as soon as the first leaf turns orange, they want Fall-themed
              everything, especially if it's limited edition. If you’ve already planned
              special seasonal product offerings for Q4 – perfect, now you need a
              comprehensive plan to market them and ensure they’re getting in front of
              the right shoppers <em>right now</em>. If you haven’t pre-planned anything,
              you may still have an opportunity to do so, depending on your product or
              service.
            </p>
            <p>
              For example, if you run a cookie subscription box then a) please send us
              the details so we can get on the list immediately and b) it's probably not
              too late to plan a winter-themed box full of gingerbread, peppermint, and
              more. If you provide professional services such as dental care, your
              seasonal product offering could instead be offering a tooth whitening
              package for those holiday parties which, according to all accounts, will
              be making a reappearance this year. If it is too late to incorporate a
              seasonal product or two into your Q4 lineup, now is the perfect time to
              take note of products that your competitors are offering or products that
              jump out to you as the perfect fit and start planning now for next Fall.
            </p>

            <h2>Holiday Shopping Season Promotions</h2>
            <p>
              Whether you have seasonal-themed products or offerings or not, there is
              always time to run some promotions that tie into the season. While big
              deal days like Black Friday and, for our fellow Canadians, Boxing Day, are
              obvious choices and should definitely be considered, there are a lot of
              other opportunities for unique promotions this quarter.
            </p>
            <p><strong>Here are some ideas for promotions you could run:</strong></p>
            <ul>
              <li>
                If you have a sample or travel-sized version of your product, consider
                offering a special where loyal customers can purchase a pack of 5
                sample/travel-sizes as stocking stuffers at a special price – not only
                does this bump their total order value, but it also gets your product
                into the hands of more potential future customers.
              </li>
              <li>
                Consider a surprise gift with purchase, especially for first-time
                customers. Customers are more likely to make a repeat purchase from you
                if they feel delighted when they receive their order, and you create a
                lasting impression – and it is the gift-giving season after all!
              </li>
              <li>
                Tap into smaller movements like Giving Tuesday or Small Business Saturday
                instead of discounting your product on Black Friday. Instead of
                devaluing your product, <em>add</em> value to it and your business in the
                eyes of your consumers by supporting a local charity or organization.
              </li>
            </ul>

            <h2>Holiday Product Shoots</h2>
            <p>
              If you know what you want to sell and how you want to promote it – it’s
              time to dive into the visuals! A Fall and Holiday photoshoot is a
              cost-effective way to get all your seasonal promotional photos done at once,
              so that you have a rich library to pull from all season long. It also
              ensures that your visual assets are cohesive across all platforms and
              touchpoints and helps to give your holiday shopping season campaigns a
              solid identity.
            </p>
            <p><strong>There are a couple things to keep in mind while planning your photoshoot:</strong></p>
            <ul>
              <li>Do you want it to just focus on the photo with some seasonal props or do you want models involved to make it feel more alive?</li>
              <li>When do you need to launch your Fall campaign? Depending on your product offerings, this could be November 1st or it could have been 3 weeks ago. Turnaround time is going to be key.</li>
              <li>Do you already have a specific campaign feel in mind or do you want help from a marketing consultant and stylist?</li>
            </ul>

            <p>
              We offer seasonal product photoshoots at The Simple Department (locals and
              non-locals welcome!), and there are also lots of other options online and
              in most urban areas that vary hugely in price and options. Feel free to{" "}
              <a className="link-darkblue" href="/contact">start a chat with one of our Project Managers</a> if you would like to know more!
            </p>

            <h2>Holiday Creator Campaigns</h2>
            <p>
              Have you considered working with a creator (or several) to make your Q4 as
              profitable as possible? While many of the biggest (and most expensive)
              creators will already be fully booked for this busy season, there are a huge
              number of niche and micro creators who create beautiful content and have
              highly engaged audiences. Creators bring excellent value to the table – not
              only do they create gorgeous, on-target content, but they also have a built-in
              audience to share it with. The content that they create is usually shareable
              beyond their profile (but ensure you check the contract you’re working with
              for fine print), which means it can be used for in-house ads and campaigns,
              in addition to sharing on the creator’s profile.
            </p>
            <p>
              When it comes to working with creators, the key is to find the right type
              of creator who is creating content that resonates with your brand and speaks
              to your specific audience. Then, once you’ve found the right creators, it
              can take time and dedication to build a good relationship with them – which
              can develop into a long-term partnership if their content and audience
              generate strong awareness, sales, and/or reviews. That’s where we come in!
              We’ve worked with hundreds of creators across a variety of industries and are
              armed with the best tools on the market to find and develop relationships
              with the right creators for your brand.{" "}
              <a className="link-darkblue" href="/contact">Get in touch</a> with us to learn how!
            </p>

            <h2>It’s Never Too Late to Plan</h2>
            <p>
              There are a lot of moving pieces to the holiday shopping season, but it
              doesn’t have to be overwhelming, and you don’t have to miss this opportunity
              to bring in additional revenue and reach new audiences. Reach out to us if
              you’d like help planning your holiday campaigns, or just want to book a
              strategy session with a <a href="/services/cmo/">fractional CMO</a>.
            </p>

            <p>
              <em>
                Don’t forget to keep your eyes out for part two of this series in a couple
                weeks, where we will be covering some of the technical considerations to
                keep in mind this holiday shopping season.
              </em>
            </p>

            <div>
              <a href="/blog" className="back-btn-resource">
                back
              </a>
            </div>
          </div>
        </section>
      </div>
    </main>
  </Layout>
);

export default BlogPostHolidayShoppingSeason;
